import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { FieldIn, FieldSel, FieldArea, Modal, Badge } from "./Fields";
import { Send, Pencil } from "lucide-react";
import { toast } from "sonner";

export const SUB_STATUS = {
  submitted: { l: "Submitted", cls: "bg-slate-100 text-slate-700" },
  under_review: { l: "Under Review", cls: "bg-blue-100 text-blue-700" },
  interview_scheduled: { l: "Interview Scheduled", cls: "bg-amber-100 text-amber-700" },
  selected: { l: "Selected", cls: "bg-emerald-100 text-emerald-700" },
  joined: { l: "Joined", cls: "bg-emerald-600 text-white" },
  on_hold: { l: "On Hold", cls: "bg-orange-100 text-orange-700" },
  rejected: { l: "Rejected", cls: "bg-rose-100 text-rose-700" },
};
const OPTS = Object.entries(SUB_STATUS).map(([v, s]) => ({ v, l: s.l }));
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function ClientSubmissions({ jobs, onOpenProfile, refreshKey }) {
  const [subs, setSubs] = useState([]);
  const [status, setStatus] = useState("");
  const [jobId, setJobId] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/client-submissions", { params: { ...(status && { status_filter: status }), ...(jobId && { job_id: jobId }) } });
    setSubs(r.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, jobId, refreshKey]);

  return (
    <div className="space-y-4" data-testid="client-submissions">
      <div className="ae-card p-4 flex flex-wrap gap-3 items-center">
        <Send className="w-4 h-4 text-[#C5A059]" />
        <div className="text-sm font-medium text-[#0F2A4A] mr-auto">Client Submissions <span className="text-slate-400">({subs.length})</span> <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-2">Private · internal only</span></div>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="cs-filter-status"
          className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none">
          <option value="">All statuses</option>{OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
        <select value={jobId} onChange={e => setJobId(e.target.value)} data-testid="cs-filter-job"
          className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none max-w-[240px]">
          <option value="">All jobs</option>{jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
        </select>
      </div>

      <div className="ae-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>{["Candidate", "Job", "Client", "Submitted", "Status", "Interview", "Client Feedback", ""].map(h => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {subs.map(s => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`cs-row-${s.id}`}>
                <td className="px-4 py-3">
                  <button onClick={() => s.profile_id && onOpenProfile(s.profile_id)} className="font-medium text-[#0F2A4A] hover:text-[#C5A059] text-left" data-testid={`cs-cand-${s.id}`}>{s.candidate_name}</button>
                  <div className="text-[11px] text-slate-400">{s.candidate_phone || s.candidate_email}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">{s.job_title}</td>
                <td className="px-4 py-3 text-slate-600">{s.client_name}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmt(s.submitted_at)}<div className="text-[10px] text-slate-400">by {s.submitted_by}</div></td>
                <td className="px-4 py-3"><Badge cls={SUB_STATUS[s.status]?.cls} tid={`cs-status-${s.id}`}>{SUB_STATUS[s.status]?.l}</Badge></td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{s.interview_date ? new Date(s.interview_date).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                <td className="px-4 py-3 text-slate-600 text-xs max-w-[220px]">{s.client_feedback || "—"}</td>
                <td className="px-4 py-3">
                  <button onClick={() => setEditing(s)} data-testid={`cs-edit-${s.id}`}
                    className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:border-[#C5A059] inline-flex items-center gap-1.5"><Pencil className="w-3 h-3" /> Update</button>
                </td>
              </tr>
            ))}
            {subs.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">No client submissions yet. Use “Submit to Client” on a pipeline card or candidate profile.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <EditSubmission sub={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EditSubmission({ sub, onClose, onSaved }) {
  const [f, setF] = useState({ status: sub.status, interview_date: (sub.interview_date || "").slice(0, 16),
    joining_date: (sub.joining_date || "").slice(0, 10), client_feedback: sub.client_feedback || "", remarks: sub.remarks || "" });
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const save = async () => {
    try {
      const payload = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      await api.patch(`/client-submissions/${sub.id}`, payload);
      toast.success("Submission updated"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  return (
    <Modal title={`Update — ${sub.candidate_name} @ ${sub.client_name}`} onClose={onClose} testid="cs-edit-modal">
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <FieldSel label="Status" val={f.status} onChange={set("status")} opts={OPTS} tid="cs-edit-status" />
          <FieldIn label="Interview Date & Time" type="datetime-local" val={f.interview_date} onChange={set("interview_date")} tid="cs-edit-interview" />
          <FieldIn label="Joining Date" type="date" val={f.joining_date} onChange={set("joining_date")} tid="cs-edit-joining" />
        </div>
        <FieldArea label="Client Feedback" val={f.client_feedback} onChange={set("client_feedback")} tid="cs-edit-feedback" />
        <FieldArea label="Internal Remarks" val={f.remarks} onChange={set("remarks")} tid="cs-edit-remarks" />
        {sub.history?.length > 0 && (
          <div className="rounded-xl bg-slate-50 p-3 space-y-1.5" data-testid="cs-history">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Timeline</div>
            {sub.history.map((h, i) => (
              <div key={i} className="text-xs text-slate-600"><b className="text-[#0F2A4A]">{SUB_STATUS[h.status]?.l}</b> · {fmt(h.at)} · {h.by}{h.note ? ` — ${h.note}` : ""}</div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={onClose} className="ae-btn-outline text-sm flex-1">Cancel</button>
          <button onClick={save} className="ae-btn-primary text-sm flex-1" data-testid="cs-edit-save">Save Update</button>
        </div>
      </div>
    </Modal>
  );
}
